/**
 * targetedIntentResolver.js
 * 
 * 多模态指代消歧与目标意图解析器：
 * 负责将用户语音输入结合当前屏幕手势锁定的 selectedTarget，
 * 完成指代消歧（“这个水库”、“它”、“当前这个”），生成标准化的 TargetedIntent。
 */

import { findReservoirByName, listReservoirs } from './reservoirCatalog.js'

export const PRONOUNS = Object.freeze([
  '这个水库',
  '该水库',
  '此水库',
  '当前水库',
  '选中水库',
  '锁定水库',
  '本水库',
  '当前目标',
  '这个',
  '这里',
  '它',
  '当前这个',
])

export const INTENT_TYPES = Object.freeze({
  FORECAST: 'reservoir.forecast',
  QUERY: 'reservoir.query',
  UNLOCK: 'target.unlock',
  UNKNOWN: 'unknown',
})

/**
 * 检查文本是否包含指代表达
 */
export function containsPronoun(text = '') {
  const str = String(text || '').trim()
  if (!str) return false
  return PRONOUNS.some(pronoun => str.includes(pronoun))
}

/**
 * 检查是否为取消选择/解除锁定意图
 */
export function isUnlockIntent(text = '') {
  const str = String(text || '').trim()
  return /(取消选择|解除锁定|取消锁定|清除选择|释放目标|取消目标|退出锁定)/.test(str)
}

/**
 * 检查是否为水库流量/来水预测意图
 */
export function isForecastIntent(text = '') {
  const str = String(text || '').trim()
  if (!str) return false
  const hasForecastKeyword = /(预测|预报|推演|演算|模拟)/.test(str)
  const hasFlowKeyword = /(流量|来水|出流|入流|入库|出库|水情|水位|径流|未来)/.test(str)
  // “预测一下这个水库未来的流量” / “预测流量” / “来水预测”
  return (hasForecastKeyword && hasFlowKeyword) || /(流量预测|来水预测|径流预报|未来流量|未来来水|流量预报)/.test(str)
}

/**
 * 从文本中提取预测时效（如“未来24小时”，“6小时”），未说明则返回 null
 */
export function extractForecastHorizon(text = '') {
  const str = String(text || '').trim()
  const match = str.match(/(\d+)\s*(?:个)?(?:小时|h|H|天|日)/)
  if (!match) return null
  const num = parseInt(match[1], 10)
  if (/(?:天|日)/.test(match[0])) return num * 24
  return num
}

/**
 * 核心解析函数：输入语音文本与当前多模态上下文，输出结构化 TargetedIntent
 * 
 * @param {string} utterance 用户输入的原始语音/文本
 * @param {Object} context 多模态上下文 (multimodalContext.snapshot())
 * @returns {Object} 标准化 TargetedIntent 或带有错误码的解析结果
 */
export function resolveTargetedIntent(utterance = '', context = {}) {
  const text = String(utterance || '').trim()
  if (!text) {
    return {
      intent: INTENT_TYPES.UNKNOWN,
      utterance: '',
      target: null,
      parameters: {},
      source: ['voice'],
    }
  }

  // 1. 解除锁定意图
  if (isUnlockIntent(text)) {
    return {
      intent: INTENT_TYPES.UNLOCK,
      utterance: text,
      target: context.target || null,
      parameters: {},
      source: ['voice'],
    }
  }

  const hasPronoun = containsPronoun(text)
  const isForecast = isForecastIntent(text)
  const activeTarget = context.target || null // 当前通过握拳锁定的 selectedTarget

  // 2. 流量预测意图
  if (isForecast) {
    const horizon = extractForecastHorizon(text)

    // 情况 A：包含指代词（如“预测一下这个水库未来的流量”）
    if (hasPronoun) {
      if (!activeTarget) {
        return {
          intent: INTENT_TYPES.FORECAST,
          error: 'NO_TARGET_LOCKED',
          message: '请先指向并锁定目标水库。',
          utterance: text,
          target: null,
          parameters: { horizon },
          source: ['voice'],
        }
      }

      return {
        intent: INTENT_TYPES.FORECAST,
        utterance: text,
        target: {
          type: 'reservoir',
          entityId: activeTarget.entityId || activeTarget.id,
          businessId: activeTarget.id || activeTarget.businessId,
          displayName: activeTarget.name || activeTarget.displayName,
          data: activeTarget.data || {},
        },
        parameters: {
          horizon: horizon, // 未提供时保持为 null，不随意猜测
          metric: 'inflow',
        },
        source: ['gesture', 'voice', 'page_context'],
        timestamp: new Date().toISOString(),
      }
    }

    // 情况 B：未包含指代词，但提到了具体水库名称（如“预测薛峰水库未来流量”）
    const mentionedReservoir = findReservoirByName(text)
    if (mentionedReservoir) {
      return {
        intent: INTENT_TYPES.FORECAST,
        utterance: text,
        target: {
          type: 'reservoir',
          entityId: mentionedReservoir.id,
          businessId: mentionedReservoir.id,
          displayName: mentionedReservoir.name,
          data: mentionedReservoir,
        },
        parameters: {
          horizon: horizon,
          metric: 'inflow',
        },
        source: ['voice', 'page_context'],
        timestamp: new Date().toISOString(),
      }
    }

    // 情况 C：既无指代词也无明确水库名，但当前有锁定水库且在谈论预测
    if (activeTarget) {
      return {
        intent: INTENT_TYPES.FORECAST,
        utterance: text,
        target: {
          type: 'reservoir',
          entityId: activeTarget.entityId || activeTarget.id,
          businessId: activeTarget.id || activeTarget.businessId,
          displayName: activeTarget.name || activeTarget.displayName,
          data: activeTarget.data || {},
        },
        parameters: {
          horizon: horizon,
          metric: 'inflow',
        },
        source: ['gesture', 'voice', 'page_context'],
        timestamp: new Date().toISOString(),
      }
    }

    // 无法确定目标水库
    return {
      intent: INTENT_TYPES.FORECAST,
      error: 'NO_TARGET_LOCKED',
      message: '请先指向并锁定目标水库。',
      utterance: text,
      target: null,
      parameters: { horizon },
      source: ['voice'],
    }
  }

  // 3. 通用水库状态/参数查询意图
  if (hasPronoun) {
    if (!activeTarget) {
      return {
        intent: INTENT_TYPES.QUERY,
        error: 'NO_TARGET_LOCKED',
        message: '请先指向并锁定目标水库。',
        utterance: text,
        target: null,
        parameters: {},
        source: ['voice'],
      }
    }

    return {
      intent: INTENT_TYPES.QUERY,
      utterance: text,
      target: {
        type: 'reservoir',
        entityId: activeTarget.entityId || activeTarget.id,
        businessId: activeTarget.id || activeTarget.businessId,
        displayName: activeTarget.name || activeTarget.displayName,
        data: activeTarget.data || {},
      },
      parameters: {},
      source: ['gesture', 'voice', 'page_context'],
      timestamp: new Date().toISOString(),
    }
  }

  // 4. 其他常规意图
  return {
    intent: INTENT_TYPES.UNKNOWN,
    utterance: text,
    target: activeTarget,
    parameters: {},
    source: ['voice'],
  }
}

export default resolveTargetedIntent
